import axios from "axios";
import httpDao from "../infrastructure/httpDao";
import {
  ChatAttachmentDownloadUrl,
  ChatAttachmentUploadTicket,
  ChatRoom,
  ChatMessage,
  ChatIdentity,
  CoachDirectOption,
  ChatWorkflowStatus,
  CreateChatRoomDto,
  OfficialInboxParentOption,
} from "../types/chat";

export const chatRepository = {
  // チャットルーム一覧取得
  async getRooms(): Promise<ChatRoom[]> {
    const res = await httpDao.get<ChatRoom[]>("/chat/rooms");
    return res.data;
  },

  // メッセージ一覧取得
  async getMessages(
    roomId: string,
    limit = 50,
    offset = 0,
    beforeMessageId?: string
  ): Promise<ChatMessage[]> {
    const res = await httpDao.get<ChatMessage[]>(
      `/chat/rooms/${roomId}/messages`,
      {
        params: {
          limit,
          ...(beforeMessageId ? { beforeMessageId } : { offset }),
        },
      }
    );
    return res.data;
  },

  /**
   * メッセージ送信（REST 経路）。
   * Socket emit は fire-and-forget で失敗を握りつぶすため、信頼性の高い REST を主経路にする。
   * サーバ側で保存後に WebSocket ブロードキャストするため、ソケットを購読中のクライアントも
   * 通常通り newMessage を受信できる。
   */
  async sendMessage(
    roomId: string,
    content: string,
    clientMessageId: string
  ): Promise<ChatMessage> {
    const res = await httpDao.post<ChatMessage>(
      `/chat/rooms/${roomId}/messages`,
      { content, clientMessageId }
    );
    return res.data;
  },

  async createAttachmentUploadUrl(
    roomId: string,
    file: File
  ): Promise<ChatAttachmentUploadTicket> {
    const res = await httpDao.post<ChatAttachmentUploadTicket>(
      `/chat/rooms/${roomId}/attachments/upload-url`,
      {
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      },
      { headers: { "x-skip-loading-overlay": "true" } }
    );
    return res.data;
  },

  async uploadAttachment(
    ticket: ChatAttachmentUploadTicket,
    file: File,
    onProgress?: (percentage: number) => void
  ): Promise<void> {
    await axios.put(ticket.uploadUrl, file, {
      headers: ticket.headers,
      onUploadProgress: ({ loaded, total }) => {
        const denominator = total ?? file.size;
        if (denominator > 0) {
          onProgress?.(Math.min(100, Math.round((loaded / denominator) * 100)));
        }
      },
    });
  },

  async completeAttachment(
    roomId: string,
    attachmentId: string,
    data: { clientMessageId?: string; content?: string }
  ): Promise<ChatMessage> {
    const res = await httpDao.post<ChatMessage>(
      `/chat/rooms/${roomId}/attachments/${attachmentId}/complete`,
      data,
      { headers: { "x-skip-loading-overlay": "true" } }
    );
    return res.data;
  },

  async getAttachmentDownloadUrl(
    messageId: string
  ): Promise<ChatAttachmentDownloadUrl> {
    const res = await httpDao.get<ChatAttachmentDownloadUrl>(
      `/chat/messages/${messageId}/attachment/download-url`
    );
    return res.data;
  },

  // リアクション追加（トグル処理）
  async addReaction(roomId: string, messageId: string, emoji: string) {
    const res = await httpDao.post(
      `/chat/rooms/${roomId}/messages/${messageId}/reactions`,
      {
        emoji,
      }
    );
    return res.data;
  },

  // リアクション削除
  async removeReaction(roomId: string, messageId: string, emoji: string) {
    await httpDao.delete(
      `/chat/rooms/${roomId}/messages/${messageId}/reactions/${emoji}`
    );
  },

  // チャットルーム作成
  async createRoom(dto: CreateChatRoomDto): Promise<ChatRoom> {
    const res = await httpDao.post<ChatRoom>("/chat/rooms", dto);
    return res.data;
  },

  // 会員 × 施設で一意な教室公式受信箱を作成、または既存受信箱を取得する
  async createOfficialInbox(
    parentId: string,
    facilityId: string
  ): Promise<{ id: string }> {
    const res = await httpDao.post<{ id: string }>("/chat/official-inboxes", {
      parentId,
      facilityId,
    });
    return res.data;
  },

  // 教室公式受信箱を作成できる、選択施設に所属中の会員候補
  async getOfficialInboxParentOptions(
    facilityId: string,
    search?: string
  ): Promise<OfficialInboxParentOption[]> {
    const res = await httpDao.get<OfficialInboxParentOption[]>(
      "/chat/official-inboxes/parent-options",
      {
        params: {
          facilityId,
          limit: 200,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }
    );
    return res.data;
  },

  // チャットルーム既読化
  async markRoomAsRead(roomId: string, lastMessageId?: string): Promise<void> {
    await httpDao.post(`/chat/rooms/${roomId}/read`, {
      ...(lastMessageId ? { lastMessageId } : {}),
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("chat:room-read", { detail: { roomId } })
      );
    }
  },

  // 公式共有受信箱の業務状態・担当を楽観ロック付きで更新する
  async updateWorkflow(
    roomId: string,
    data: {
      status?: ChatWorkflowStatus;
      assignedStaffId?: string | null;
      expectedVersion: number;
    }
  ): Promise<ChatRoom> {
    const res = await httpDao.patch<ChatRoom>(
      `/chat/rooms/${roomId}/workflow`,
      data
    );
    return res.data;
  },

  // 未担当の公式共有受信箱を現在のスタッフが引き受ける
  async claimRoom(roomId: string, expectedVersion: number): Promise<ChatRoom> {
    const res = await httpDao.post<ChatRoom>(`/chat/rooms/${roomId}/claim`, {
      expectedVersion,
    });
    return res.data;
  },

  // 教室公式受信箱を担当できる、同一施設へのアクセス権を持つスタッフ
  async getAssignableStaff(facilityId: string): Promise<ChatIdentity[]> {
    const res = await httpDao.get<ChatIdentity[]>(
      "/chat/official-inboxes/assignable-staff",
      { params: { facilityId } }
    );
    return res.data;
  },

  // 現在のスタッフがこの施設で実際に担当している、個別チャット対象の受講者
  async getCoachDirectOptions(
    facilityId: string
  ): Promise<CoachDirectOption[]> {
    const res = await httpDao.get<CoachDirectOption[]>(
      "/chat/coach-direct-options",
      { params: { facilityId } }
    );
    return res.data;
  },

  // 自分の情報を取得（簡易用）
  async getMe(): Promise<{ id: string }> {
    // 本来はAuthRepositoryにあるべきだが、chatWindowで急遽必要になったため
    const res = await httpDao.get<{ id: string }>("/auth/staff/me");
    return res.data;
  },
};
